import React, { RefObject } from 'react';
import 'twin.macro';
import EditableTextComponent from '@teetsh/app/src/components/Form/EditableText';
import {
  removeItemAtIndex,
  replaceItemAtIndex,
} from '@teetsh/app/src/utils/listMethods';
import { Attachment } from '@teetsh/app/src/types/attachment';
import { getSeanceAttachmentUrl } from '@teetsh/app/src/utils/urls';
import Button from '@teetsh/app/src/components/Buttons/Button';
import { ArrowDownTrayIcon, TrashIcon } from '@heroicons/react/24/outline';
import { isURLAnImage } from './isUrlImage';
import { PreviewImg } from 'components/Attachments/PreviewImg';
import { printSize } from '@teetsh/app/src/utils/printSize';
import { useTranslation } from 'react-i18next';
import ButtonExternal from 'components/Buttons/ExternalLink';
import { getFileIcon } from './getFileIcon';

function SingleAttachmentComponent({
  label,
  fileUrl,
  fileSize,
  onChange,
  onDelete,
  inputRef,
  testId,
}: {
  label: string;
  fileUrl: string;
  fileSize?: number;
  onChange: (v: string) => void;
  onDelete: () => void;
  inputRef?: RefObject<HTMLInputElement>;
  testId?: string;
}) {
  const { t } = useTranslation(['common']);
  const FileIcon = getFileIcon(fileUrl);
  return (
    <div tw="flex items-center gap-x-2">
      <div tw="flex items-center px-4 py-3 flex-1 rounded-lg bg-neutral-quiet overflow-x-auto">
        <div tw="mr-2">
          {isURLAnImage(fileUrl) ? (
            <PreviewImg alt={label} fileUrl={fileUrl} />
          ) : (
            <FileIcon tw="h-5 w-5 flex-shrink-0" />
          )}
        </div>
        <div tw="flex-1">
          <EditableTextComponent
            tw="truncate text-brand-moderate font-semibold text-base"
            value={label}
            size="sm"
            testId={testId}
            onConfirm={onChange}
            inputRef={inputRef}
          />
          {fileSize && (
            <div tw="text-neutral-quiet text-sm">
              {printSize(fileSize, t('common:fileSizeLocale'))}
            </div>
          )}
        </div>
      </div>
      <ButtonExternal
        minimal
        intent="none"
        href={fileUrl}
        icon={ArrowDownTrayIcon}
      />
      <Button minimal intent="danger" onClick={onDelete} icon={TrashIcon} />
    </div>
  );
}
interface Props {
  attachments: Attachment[];
  onChange: (v: Attachment[]) => void;
  inputRef?: RefObject<HTMLInputElement>;
}

export function AttachmentComponent(props: Props) {
  const { attachments, onChange, inputRef } = props;

  if (attachments.length === 0) {
    return null;
  }
  return (
    <div tw="flex flex-col gap-y-2">
      {attachments.map((attachment, index) => {
        const { s3Path, s3Filename, label } = attachment;
        return (
          <SingleAttachmentComponent
            key={s3Filename}
            inputRef={inputRef}
            label={label}
            fileUrl={getSeanceAttachmentUrl(s3Path, s3Filename, label)}
            fileSize={attachment.fileSize}
            testId={`input-attachment-label-${index}`}
            onChange={newLabel =>
              onChange(
                replaceItemAtIndex(
                  attachments,
                  { ...attachment, label: newLabel },
                  index,
                ),
              )
            }
            onDelete={() => onChange(removeItemAtIndex(attachments, index))}
          />
        );
      })}
    </div>
  );
}
